import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { DateTime, Duration } from "luxon";
import { useParams } from "react-router-dom";
import { shallow } from "zustand/shallow";

import { getMeetingReport } from "@app/analytics/api/meetingReport";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import { SpeakerEvent, TalkTimeData } from "@app/analytics/MeetingReport/types";
import { getSpeakerEvents } from "@app/analytics/MeetingReport/utils/eventUtils";
import { getRoundedValues } from "@app/analytics/utils";
import { MeetingReport, TranscriptSpeaker } from "@app/common/types";

import useTranscriptQuery from "./useTranscriptQuery";

const DEFAULT_OPTS = { enabled: true };

interface UseMeetingReport {
  isError: boolean;
  isLoading: boolean;
  meetingDuration?: Duration;
  meetingReport?: MeetingReport;
  meetingStartTime?: DateTime;
  meetingEndTime?: DateTime;
  speakerEvents: SpeakerEvent[];
}

const getSpeakersFromTalkTimeData = (
  talkTimeData?: TalkTimeData[]
): TranscriptSpeaker[] => {
  if (!talkTimeData) return [];

  return talkTimeData.map((data) => ({
    name: data.name,
    talkTimePercent: getRoundedValues(data.talkTimePercent, "%").formattedValue,
    rawTalkTimePercent: data.talkTimePercent,
  }));
};

const useMeetingReport = (opts = DEFAULT_OPTS): UseMeetingReport => {
  const { sessionId } = useParams();
  const {
    data: { isRestrictedReport },
  } = useSessionMetadata(opts);

  const queryOpts = { enabled: opts.enabled && !isRestrictedReport };

  const { isLoading: isTranscriptLoading, data: transcriptData } =
    useTranscriptQuery(queryOpts);

  // if we're viewing the upsell preview, we can't rely on the turns to generate speaker
  // events since we won't have all the turns. if the turns are empty, speaker events will
  // be generated from the talk turns
  const transcriptTurns = isRestrictedReport ? [] : transcriptData?.turns ?? [];

  const { setSpeakersWithTalkTime } = useTranscriptStore(
    (state) => ({
      allSpeakers: state.allSpeakers,
      setSpeakersWithTalkTime: state.setSpeakersWithTalkTime,
    }),
    shallow
  );

  const {
    isLoading: isMeetingReportLoading,
    data: meetingReport,
    isError,
  } = useQuery({
    queryKey: ["meetingReport", sessionId],
    queryFn: () => getMeetingReport(sessionId!),
    enabled: queryOpts.enabled && Boolean(sessionId),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const speakersWithTalkTime = useMemo(
    () => getSpeakersFromTalkTimeData(meetingReport?.talkTimeData),
    [meetingReport?.talkTimeData]
  );
  useEffect(
    () => setSpeakersWithTalkTime(speakersWithTalkTime),
    [speakersWithTalkTime]
  );

  const meetingStartTime = meetingReport?.rawStartTime
    ? DateTime.fromMillis(meetingReport?.rawStartTime)
    : undefined;

  const meetingEndTime = meetingReport?.rawEndTime
    ? DateTime.fromMillis(meetingReport?.rawEndTime)
    : undefined;

  const meetingDuration =
    meetingReport?.rawStartTime && meetingReport?.rawEndTime
      ? Duration.fromMillis(
          meetingReport.rawEndTime - meetingReport.rawStartTime
        )
      : undefined;

  const isDataLoading = isTranscriptLoading || isMeetingReportLoading;

  const speakerEvents = useMemo(() => {
    if (isDataLoading || isRestrictedReport) return [];
    return getSpeakerEvents(transcriptTurns, meetingReport);
  }, [isDataLoading, isRestrictedReport, meetingReport]);

  return {
    isError,
    isLoading: isMeetingReportLoading || isTranscriptLoading,
    meetingDuration,
    meetingReport,
    meetingStartTime,
    meetingEndTime,
    speakerEvents,
  };
};

export default useMeetingReport;
